import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { PotentialClient, PotentialClientOrigin } from 'src/app/models/potential-client.interface';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-add-potential-client-dialog',
  templateUrl: './add-potential-client-dialog.component.html',
  styleUrls: ['./add-potential-client-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatOption,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatIcon,
    MatSuffix,
    MatHint,
    MatDialogActions,
    MatButton,
  ],
})
export class AddPotentialClientDialogComponent implements OnInit {
  protected fb = inject(FormBuilder);
  dialogRef = inject<MatDialogRef<AddPotentialClientDialogComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  private api = inject(ApiService);

  form: FormGroup;
  available_comercials: User[] = [];
  origins: PotentialClientOrigin[] = [];
  filtered_origins: PotentialClientOrigin[] = [];
  originSearchControl = new UntypedFormControl('');
  comercialSelectControl = new UntypedFormControl();
  is_edit = false;
  saving = false;
  create_origin_label: string | null = null;

  constructor() {
    const data = this.data;

    const potential_client: PotentialClient | null = data != null ? data.potential_client : null;
    this.is_edit = potential_client != null;

    if (this.is_edit) {
      this.form = this.fb.group({
        full_name: [potential_client!.full_name, Validators.required],
        company_name: [potential_client!.company_name],
        email: [potential_client!.email, Validators.email],
        phone: [potential_client!.phone],
        origin_id: [potential_client!.origin_id, Validators.required],
        commercial_id: [potential_client!.commercial_id, Validators.required],
        approximate_company_annual_billing_in_millions: [potential_client!.approximate_company_annual_billing_in_millions],
        description: [potential_client!.description],
      });
    } else {
      this.form = this.fb.group({
        full_name: ['', Validators.required],
        email: ['', Validators.email],
        phone: [''],
        origin_id: [null, Validators.required],
      });
    }
  }

  ngOnInit(): void {
    this.fetchOrigins();
    if (this.is_edit) {
      this.listenComercialSearch();
      if (this.data?.potential_client?.commercial != null) {
        this.available_comercials = [this.data.potential_client.commercial];
        this.comercialSelectControl.patchValue(this.data.potential_client.commercial);
      }
    }
    this.originSearchControl.valueChanges.subscribe((value: string | PotentialClientOrigin) => {
      this.filterOrigins(value);
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  selectComercial(c: User) {
    this.form.patchValue({ commercial_id: c.id });
  }

  displayFnComercial(c: User) {
    if (c != null) return c.name + ' ' + c.surnames;
    else return '';
  }

  displayFnOrigin(origin: PotentialClientOrigin | string | { __create?: boolean; name?: string }): string {
    if (origin == null) return '';
    if (typeof origin === 'string') return origin;
    return origin.name ?? '';
  }

  selectOrigin(origin: PotentialClientOrigin | { __create: true; name: string }) {
    if (origin && '__create' in origin) {
      this.createOrigin(origin.name);
      return;
    }
    this.form.patchValue({ origin_id: origin.id });
    this.originSearchControl.setValue(origin, { emitEvent: false });
    this.create_origin_label = null;
  }

  submit() {
    if (this.saving) return;

    if (!this.is_edit && !this.form.value.email && !this.form.value.phone) {
      this.form.get('email')?.setErrors({ requiredContact: true });
      return;
    }

    if (this.form.value.origin_id) {
      if (!this.form.valid) return;
      this.dialogRef.close(this.form.value);
      return;
    }

    const typed = typeof this.originSearchControl.value === 'string' ? this.originSearchControl.value.trim() : '';
    if (typed) {
      this.createOrigin(typed, true);
    }
  }

  private createOrigin(name: string, closeAfter = false) {
    this.saving = true;
    this.api.createPotentialClientOrigin({ name }).subscribe({
      next: origin => {
        this.applyOrigin(origin, closeAfter);
      },
      error: () => {
        const existing = this.origins.find(o => o.name.localeCompare(name, undefined, { sensitivity: 'accent' }) === 0);
        if (existing != null) {
          this.applyOrigin(existing, closeAfter);
          return;
        }
        this.saving = false;
      },
    });
  }

  private applyOrigin(origin: PotentialClientOrigin, closeAfter = false) {
    if (!this.origins.find(o => o.id === origin.id)) {
      this.origins = [...this.origins, origin].sort((a, b) => a.name.localeCompare(b.name));
    }
    this.form.patchValue({ origin_id: origin.id });
    this.originSearchControl.setValue(origin, { emitEvent: false });
    this.create_origin_label = null;
    this.filtered_origins = this.origins;
    this.saving = false;
    if (closeAfter && this.form.valid) {
      this.dialogRef.close(this.form.value);
    }
  }

  private fetchOrigins() {
    this.api.getPotentialClientOrigins().subscribe(data => {
      this.origins = Array.isArray(data?.data) ? data.data : [];
      this.filtered_origins = this.origins;
      const selectedId = this.form.value.origin_id;
      if (selectedId != null) {
        const selected = this.origins.find(o => o.id === selectedId);
        if (selected) this.originSearchControl.setValue(selected, { emitEvent: false });
      }
    });
  }

  private filterOrigins(value: string | PotentialClientOrigin) {
    const search = typeof value === 'string' ? value.trim().toLowerCase() : (value?.name?.toLowerCase() ?? '');
    this.filtered_origins = this.origins.filter(o => o.name.toLowerCase().includes(search));
    const exact = this.origins.find(o => o.name.toLowerCase() === search);
    this.create_origin_label = search && !exact ? (typeof value === 'string' ? value.trim() : null) : null;
    if (typeof value !== 'string' && value?.id) {
      this.form.patchValue({ origin_id: value.id });
    } else if (typeof value === 'string') {
      this.form.patchValue({ origin_id: exact?.id ?? null });
    }
  }

  private listenComercialSearch() {
    this.comercialSelectControl.valueChanges.subscribe((search_string: string) => {
      if (search_string != null && typeof search_string === 'string' && search_string.length >= 3) {
        this.api.getUsers({ search: search_string }).subscribe(data => {
          this.available_comercials = data.data;
        });
      } else if (typeof search_string === 'string') {
        this.available_comercials = [];
      }
    });
  }
}
